import React, { useEffect, useState } from 'react';
import { Switch, NavLink, useRouteMatch, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PrivateRoute from '../../components/shared/PrivateRoute';
import DashboardLayout from '../../layouts/DashoardLayout';
import { Box, Paper, Typography, List, ListItemButton, IconButton } from '../../ui/core';
import { SearchIcon } from '../../ui/icons';
import { flushPageSx, surfaceCardSx, cardHeaderSx, hairline, railItemSx } from '../../ui/styles';

import UsersTable from './UsersTable';
import UsersSearch from '../../components/widgets/Users/UsersSearch';
import SearchBar from '../../components/shared/SearchBar';
import { fetchUsersCount } from '../../redux/Users/actions';

// Secondary panel — mirrors the Driver Approvals rail: a section list card
// beside the content card, and the persistent search toggle in its header.
const UsersRail = ({ searchActive, onToggleSearch }) => {
  const inSection = !!useRouteMatch({ path: '/users' });

  return (
    <Paper elevation={0} sx={{ ...surfaceCardSx, width: { xs: '100%', md: 240 }, flexShrink: 0 }}>
      <Box
        sx={{ p: '16px', pb: '12px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>People</Typography>
        <IconButton
          size="small"
          onClick={onToggleSearch}
          aria-label="Search users"
          sx={{ color: searchActive ? 'text.primary' : 'text.secondary' }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, px: '8px', pb: '8px', overflowY: 'auto' }}>
        <List disablePadding>
          <ListItemButton component={NavLink} to="/users" selected={inSection} disableRipple sx={railItemSx}>
            All users
          </ListItemButton>
        </List>
      </Box>
    </Paper>
  );
};

// `flush` gives every section the shared master-nav rail + gray page
// background. The users list and a user's detail page are both full-page routes
// that share a single content card; the People rail sits beside it and owns the
// persistent search, which opens as a full-width bar over the content.
const UsersShell = () => {
  const dispatch = useDispatch();
  const [searchOpen, setSearchOpen] = useState(false);
  const isTableRoute = !!useRouteMatch({ path: '/users', exact: true });
  const location = useLocation();

  useEffect(() => {
    fetchUsersCount()(dispatch);
  }, [dispatch]);

  // Dismiss the search bar on any navigation (e.g. clicking a result), so it
  // doesn't linger above the content after you've moved to a user's profile.
  useEffect(() => {
    setSearchOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={flushPageSx}>
      <UsersRail searchActive={searchOpen} onToggleSearch={() => setSearchOpen((open) => !open)} />
      <Paper
        elevation={0}
        sx={{ ...surfaceCardSx, flex: 1, minWidth: 0, borderLeft: (theme) => `1px solid ${hairline(theme)}` }}
      >
        {searchOpen && (
          <SearchBar onClose={() => setSearchOpen(false)}>
            <UsersSearch variant="bar" width="100%" autoFocus placeholder="Search users by name or email" />
          </SearchBar>
        )}
        {isTableRoute && (
          <Box sx={cardHeaderSx}>
            <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em' }}>Users</Typography>
          </Box>
        )}
        {/* Only the list. A user's own page is its own flush route now
            (pages/UserProfile, registered in App.js) rather than a route inside this
            shell: its Driver tab opens a document inspector that needs the full width,
            and the 240px People rail was 240px the scan could not have. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Switch>
            <PrivateRoute exact path="/users" component={UsersTable} />
          </Switch>
        </Box>
      </Paper>
    </Box>
  );
};

const Users = () => (
  <DashboardLayout flush title="Users">
    <UsersShell />
  </DashboardLayout>
);

export default Users;
